import services from "@/configs/axios";
import CONFIGS from "@/constants/env";
import {
  IAgentData,
  IAgentFilters,
  IFormCreateData,
} from "@/interfaces/IAgentData";
import { IResponseFormat } from "@/interfaces/IHelper";
import { IProfile } from "@/interfaces/IProfile";

export const getAgents = async (
  props: IAgentFilters,
): Promise<IResponseFormat<IAgentData[]>> => {
  const data = {
    limit: props.limit,
    page: props.page,
    sort: props.sort,
    sort_option: props.sort_option,
    search: props.search,
    search_option: props.search_option,
    is_active: props.is_active,
    skip: props.skip,
    tags: props.tags,
  };
  try {
    const response = await fetch(`${CONFIGS.API}/agents/all`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      cache: "no-cache",
    });

    // If the response is not OK, return data error type IAgentData
    if (!response.ok) {
      return {
        data: [],
        message: "Failed to fetch agents",
        status: false,
      };
    }

    // If the response is OK, return the data type IAgentData
    const result = (await response.json()) as IResponseFormat<IAgentData[]>;
    return result;
  } catch {
    return {
      data: [],
      message: "Failed to fetch agents",
      status: false,
    };
  }
};

export const createAgent = async (
  data: Omit<IFormCreateData, "file_path" | "character">,
) => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("description", data.description ?? "");
  formData.append(
    "image",
    data.image instanceof File
      ? data.image
      : data.image
        ? data.image.toString()
        : "",
  );
  formData.append("prompt", data.prompt ?? "");
  if (
    Array.isArray(data.variables) &&
    data.variables.some((v) => v.value?.trim() !== "")
  ) {
    formData.append("variables", JSON.stringify(data.variables));
  } else {
    formData.append("variables", JSON.stringify([]));
  }
  formData.append("mode", data.mode ?? "");
  formData.append("ai_provider", data.ai_provider ?? "");
  formData.append("ai_model", data.ai_model ?? "");
  // formData.append("filePath", data.filePath ?? "");
  const tags = [data.selectedTag1, data.selectedTag2].filter(
    (tag) => tag?.trim() !== "",
  );

  if (tags.length > 0) {
    formData.append("tags", JSON.stringify(tags));
  }

  formData.append("publish_status", data.publish_status);

  const response = await services.post<IResponseFormat<IAgentData>>(
    `${CONFIGS.API}/agents`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const getAgentById = async (id: string) => {
  const response = await services.get<IAgentData>(
    `${CONFIGS.API}/agents/${id}`,
  );
  return response.data;
};

export const updateAgent = async (
  data: Omit<IFormCreateData, "file_path" | "character">,
) => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("description", data.description ?? "");
  formData.append("prompt", data.prompt ?? "");
  formData.append("mode", data.mode ?? "");
  formData.append("ai_provider", data.ai_provider ?? "");
  formData.append("ai_model", data.ai_model ?? "");
  // formData.append("filePath", data.filePath ?? "");
  formData.append("publish_status", data.publish_status);

  if (data.image instanceof File) {
    formData.append("image", data.image);
  }

  if (
    Array.isArray(data.variables) &&
    data.variables.some((v) => v.value?.trim() !== "")
  ) {
    formData.append("variables", JSON.stringify(data.variables));
  } else {
    formData.append("variables", JSON.stringify([]));
  }

  const tags = [data.selectedTag1, data.selectedTag2].filter(
    (tag) => tag?.trim() !== "",
  );
  if (tags.length > 0) {
    formData.append("tags", JSON.stringify(tags));
  }

  const response = await services.put<IResponseFormat<IAgentData>>(
    `${CONFIGS.API}/agents/${data._id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const updateRemixAgent = async (agent_id: string) => {
  const response = await services.post<IResponseFormat<IAgentData>>(
    `${CONFIGS.API}/agents/remix-agents`,
    {
      agent_id,
    },
  );
  return response.data;
};

export const updateFavoriteAgent = async (agent_id: string, type: string) => {
  const response = await services.put<IResponseFormat<IProfile>>(
    `${CONFIGS.API}/agents/update/agent-favorite`,
    { agent_id, type },
  );
  return response.data;
};

export const publishedAgent = async (agent_id: string) => {
  const response = await services.post(`${CONFIGS.API}/agents/publish-agent`, {
    agent_id,
  });
  return response.data;
};

export const unpublishedAgent = async (agent_id: string) => {
  const response = await services.post(
    `${CONFIGS.API}/agents/unpublish-agent`,
    {
      agent_id,
    },
  );
  return response.data;
};
export const duplicateAgent = async (agent_id: string) => {
  const response = await services.post(
    `${CONFIGS.API}/agents/duplicate-agent`,
    {
      agent_id,
    },
  );
  return response.data;
};
export const deleteAgent = async (agent_id: string) => {
  const response = await services.delete(`${CONFIGS.API}/agents/${agent_id}`);
  return response.data;
};

export const startAgent = async (prompt: string) => {
  try {
    const response = await fetch(`${CONFIGS.API}/agents/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      console.error("Failed to start agent");
    }

    return await response.json();
  } catch (error) {
    console.error("Error starting agent:", error);
    return error;
  }
};

export const shareAgents = async (agent_id: string) => {
  const response = await services.put<
    IResponseFormat<Pick<IAgentData, "shared_amount">>
  >(`${CONFIGS.API}/agents/share-agent-record/${agent_id}`);
  return response.data;
};
